/**
 * Background sync composable.
 *
 * Pushes dirty local patterns to the API and pulls server patterns back into
 * IndexedDB. Singleton — all callers share the same isSyncing/isOnline state.
 *
 * Conflict resolution strategy: last-write-wins using `updated_at` timestamps.
 * On push, an `If-Unmodified-Since` header carries the client's last-known server
 * timestamp. A 409 Conflict response means the server version is newer — the local
 * record is overwritten with the server version and the conflict is logged.
 * On pull, patterns where the server's `updatedAt` is newer than the local
 * `serverUpdatedAt` are fetched and overwritten locally.
 *
 * Sync is triggered by:
 *  - Explicit `sync()` call from a caller (e.g. App.vue on login)
 *  - The `window online` event (reconnect after going offline)
 *
 * The caller (App.vue) is responsible for triggering sync on login by watching
 * `isAuthenticated` and calling `sync()` when it becomes true.
 */

import { ref, readonly } from 'vue'
import { type Thread, type Stitch, type Backstitch, type FrenchKnot } from '@cross-my-heart/shared'
import {
  useLocalStore,
  type LocalPattern,
  type Project,
  type StitchCompletion,
} from './useLocalStore'

// Singleton state shared across all composable calls
const isSyncing = ref(false)
const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true)
const syncErrors = ref<string[]>([])
const hasPendingChanges = ref(false)

async function refreshPendingChanges(): Promise<void> {
  const store = useLocalStore()
  const [dirtyPatterns, dirtyProjects] = await Promise.all([
    store.getDirtyPatterns(),
    store.getDirtyProjects(),
  ])
  hasPendingChanges.value = dirtyPatterns.length > 0 || dirtyProjects.length > 0
}

// Wire up online/offline detection once (module load time, not per-call)
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline.value = true
    void sync()
  })
  window.addEventListener('offline', () => {
    isOnline.value = false
    // Refresh so the indicator knows if changes are waiting for reconnect
    void refreshPendingChanges()
  })
}

/** Shape of a pattern returned by GET /api/patterns/:id */
interface ServerPattern {
  id: string
  info: { title: string; description?: string | null }
  dimensions: { width: number; height: number }
  fabric: { count: number; type: string; color: string }
  palette: Thread[]
  stitches: Stitch[]
  backstitches: Backstitch[]
  frenchKnots: FrenchKnot[]
}

/** Shape of items returned by GET /api/patterns/my */
interface ServerListItem {
  id: string
  updatedAt: string | null
}

/**
 * Convert a LocalPattern to the body shape POST/PUT /api/patterns expects.
 */
function toApiBody(localPattern: LocalPattern): Record<string, unknown> {
  const p = localPattern.pattern
  return {
    title: p.info.title || localPattern.title,
    description: p.info.description ?? null,
    visibility: 'private',
    width: p.dimensions.width,
    height: p.dimensions.height,
    fabricCount: p.fabric.count,
    fabricType: p.fabric.type,
    fabricColor: p.fabric.color,
    palette: p.palette,
    stitches: p.stitches,
    backstitches: p.backstitches,
    frenchKnots: p.frenchKnots,
  }
}

/** Build a LocalPattern.pattern from a server pattern response. */
function buildLocalPattern(server: ServerPattern, serverId: string): LocalPattern['pattern'] {
  return {
    id: serverId,
    formatVersion: '1.0.0',
    info: {
      title: server.info.title,
      description: server.info.description ?? undefined,
    },
    dimensions: server.dimensions,
    fabric: server.fabric,
    palette: server.palette,
    stitches: server.stitches,
    backstitches: server.backstitches,
    frenchKnots: server.frenchKnots,
  }
}

/**
 * Push all dirty local patterns to the server. Each pattern is attempted
 * independently — a failure on one does not abort the rest.
 *
 * New patterns (no serverId) use POST. Previously synced patterns use PUT
 * with an `If-Unmodified-Since` header. A 409 Conflict response means the
 * server version is newer — the local record is overwritten and the conflict
 * is logged to the console and surfaced in syncErrors.
 */
async function pushDirty(): Promise<void> {
  const store = useLocalStore()
  const dirty = await store.getDirtyPatterns()

  for (const pattern of dirty) {
    try {
      const body = toApiBody(pattern)

      let res: Response
      if (pattern.serverId) {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Sync-Op': 'push',
        }
        if (pattern.serverUpdatedAt) {
          headers['If-Unmodified-Since'] = pattern.serverUpdatedAt
        }
        res = await fetch(`/api/patterns/${pattern.serverId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ ...body, skipVersion: false }),
        })
      } else {
        res = await fetch('/api/patterns', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      }

      if (res.status === 409) {
        const conflictData = (await res.json()) as {
          error: string
          serverUpdatedAt: string
          pattern: ServerPattern
        }
        // Snapshot local state before overwrite so the user can recover from version history.
        // Best-effort: a snapshot failure must not block the overwrite.
        try {
          await store.saveVersionSnapshot(
            pattern,
            'Local version before conflict overwrite (auto-saved)'
          )
        } catch (e) {
          console.warn('[sync] Failed to snapshot before conflict overwrite:', e)
        }
        // Server version is newer — overwrite local with server data.
        // Preserve display-only fields (symbolOverrides) and local-only fields (tags, notes)
        // since they aren't tracked on the server yet.
        // pattern.serverId is guaranteed set here — 409 only fires for PUT paths
        const serverId = pattern.serverId ?? ''
        await store.savePattern({
          ...pattern,
          title: conflictData.pattern.info.title,
          pattern: buildLocalPattern(conflictData.pattern, serverId),
          dirty: false,
          serverUpdatedAt: conflictData.serverUpdatedAt,
        })
        const msg = `Conflict on "${pattern.title}": server version is newer. Local changes overwritten — previous version saved to version history.`
        console.warn(`[sync] ${msg}`)
        syncErrors.value.push(msg)
        continue
      }

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        syncErrors.value.push(`Failed to sync "${pattern.title}": ${data.error ?? res.statusText}`)
        continue
      }

      const responseData = (await res.json()) as { id?: string; updatedAt?: string | null }
      const serverId = pattern.serverId ?? (responseData.id as string)
      await store.markSynced(pattern.id, serverId, responseData.updatedAt ?? null)
    } catch (e) {
      syncErrors.value.push(
        `Network error syncing "${pattern.title}": ${e instanceof Error ? e.message : 'unknown'}`
      )
    }
  }
}

/**
 * Fetch a pattern from the server and store it locally, optionally overwriting
 * an existing local record. Display-only fields (symbolOverrides) are preserved
 * on overwrite; the pattern data itself is replaced with the server version.
 */
async function fetchAndApplyServerPattern(
  store: ReturnType<typeof useLocalStore>,
  existingLocal: LocalPattern | null,
  serverId: string,
  serverUpdatedAt: string | null
): Promise<void> {
  // X-Sync-Op marks this as a sync-originated request. The Worker emits sync_pull
  // on GET /my (one per cycle) rather than here (one per fetched pattern).
  const res = await fetch(`/api/patterns/${serverId}`, { headers: { 'X-Sync-Op': 'pull' } })
  if (!res.ok) return

  const serverPattern = (await res.json()) as ServerPattern

  if (existingLocal) {
    await store.savePattern({
      ...existingLocal,
      title: serverPattern.info.title,
      pattern: buildLocalPattern(serverPattern, serverId),
      dirty: false,
      serverUpdatedAt,
    })
  } else {
    await store.savePattern({
      id: crypto.randomUUID(),
      serverId,
      title: serverPattern.info.title,
      pattern: buildLocalPattern(serverPattern, serverId),
      dirty: false,
      createdAt: new Date().toISOString(),
      serverUpdatedAt,
    })
  }
}

/**
 * Pull patterns from the server and merge into IndexedDB.
 * - New server patterns are added locally.
 * - Dirty local patterns are skipped (pushDirty handles conflict resolution for them).
 * - Clean local patterns where the server has a newer version are overwritten.
 * - Already-in-sync patterns are left untouched.
 *
 * Follows cursor pagination to exhaustion. If a page fetch fails mid-paginate,
 * the already-fetched pages are still processed (partial pull beats no pull).
 */
async function pullFromServer(): Promise<void> {
  const store = useLocalStore()

  const serverList: ServerListItem[] = []
  let cursor: string | null = null
  try {
    do {
      const url = cursor
        ? `/api/patterns/my?cursor=${encodeURIComponent(cursor)}`
        : '/api/patterns/my'
      const res = await fetch(url, { headers: { 'X-Sync-Op': 'pull' } })
      if (!res.ok) break
      const data = (await res.json()) as { results: ServerListItem[]; nextCursor: string | null }
      serverList.push(...data.results)
      cursor = data.nextCursor
    } while (cursor !== null)
  } catch {
    // Fall through — process whatever pages were fetched before the error
  }

  const localPatterns = await store.getAllPatterns()
  const localByServerId = new Map(
    localPatterns.filter((p) => p.serverId).map((p) => [p.serverId as string, p])
  )

  for (const { id: serverId, updatedAt: serverUpdatedAt } of serverList) {
    const local = localByServerId.get(serverId)

    try {
      if (!local) {
        await fetchAndApplyServerPattern(store, null, serverId, serverUpdatedAt)
        continue
      }

      if (local.dirty) {
        // pushDirty handles conflict resolution for dirty patterns
        continue
      }

      if (
        !local.serverUpdatedAt ||
        (serverUpdatedAt !== null && serverUpdatedAt > local.serverUpdatedAt)
      ) {
        await fetchAndApplyServerPattern(store, local, serverId, serverUpdatedAt)
      }
      // Otherwise: timestamps match — already in sync, no-op
    } catch {
      // Non-fatal: skip this pattern, try the rest
    }
  }
}

// ─── Project sync (#62) ──────────────────────────────────────────────────────
// Mirrors the pattern flow. A project references its pattern by *local* id, but
// the server stores the pattern's *server* id — so push maps local→server (and
// defers a project whose pattern hasn't synced yet), and pull maps server→local.

/** Shape of items returned by GET /api/projects/my */
interface ServerProjectListItem {
  id: string
  updatedAt: string | null
}

/** Shape returned by GET /api/projects/:id (and the 409 conflict body) */
interface ServerProject {
  id: string
  patternId: string // server pattern id
  title: string
  completions: StitchCompletion[]
  createdAt: string
  updatedAt: string
}

/**
 * Build a local Project from a server project, mapping the server pattern id
 * back to the local pattern id and preserving local-only fields (viewState,
 * photoUrls) from any existing local copy.
 */
function buildLocalProject(
  server: ServerProject,
  existingLocal: Project | null,
  localPatterns: LocalPattern[]
): Project {
  const localPatternId =
    localPatterns.find((p) => p.serverId === server.patternId)?.id ??
    existingLocal?.patternId ??
    server.patternId
  return {
    ...(existingLocal ?? ({} as Partial<Project>)),
    id: server.id,
    patternId: localPatternId,
    title: server.title,
    completions: server.completions ?? [],
    createdAt: existingLocal?.createdAt ?? server.createdAt,
    updatedAt: server.updatedAt,
    serverUpdatedAt: server.updatedAt,
    dirty: false,
  }
}

/** Push dirty local projects. New projects POST (sending the local id); previously
 *  synced ones PUT with If-Unmodified-Since, with 409 → overwrite-local. A project
 *  whose pattern isn't synced yet is skipped this cycle (it pushes once the pattern has). */
async function pushDirtyProjects(): Promise<void> {
  const store = useLocalStore()
  const dirty = await store.getDirtyProjects()
  if (dirty.length === 0) return

  const localPatterns = await store.getAllPatterns()
  const serverPatternId = new Map(localPatterns.map((p) => [p.id, p.serverId]))

  for (const project of dirty) {
    const patternServerId = serverPatternId.get(project.patternId)
    if (!patternServerId) continue // pattern not synced yet — defer

    try {
      const body = {
        patternId: patternServerId,
        title: project.title,
        completions: project.completions,
      }

      let res: Response
      if (project.serverUpdatedAt) {
        res = await fetch(`/api/projects/${project.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Sync-Op': 'push',
            'If-Unmodified-Since': project.serverUpdatedAt,
          },
          body: JSON.stringify(body),
        })
      } else {
        res = await fetch('/api/projects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Sync-Op': 'push' },
          body: JSON.stringify({ id: project.id, ...body }),
        })
      }

      if (res.status === 409) {
        const conflict = (await res.json()) as { serverUpdatedAt: string; project: ServerProject }
        await store.applyServerProject(buildLocalProject(conflict.project, project, localPatterns))
        const msg = `Conflict on project "${project.title}": server version is newer. Local changes overwritten.`
        console.warn(`[sync] ${msg}`)
        syncErrors.value.push(msg)
        continue
      }

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        syncErrors.value.push(
          `Failed to sync project "${project.title}": ${data.error ?? res.statusText}`
        )
        continue
      }

      const data = (await res.json()) as { updatedAt?: string | null }
      await store.markProjectSynced(project.id, data.updatedAt ?? null)
    } catch (e) {
      syncErrors.value.push(
        `Network error syncing project "${project.title}": ${e instanceof Error ? e.message : 'unknown'}`
      )
    }
  }
}

/** Pull the user's server projects into IndexedDB (mirrors pullFromServer). */
async function pullProjects(): Promise<void> {
  const store = useLocalStore()

  const serverList: ServerProjectListItem[] = []
  let cursor: string | null = null
  try {
    do {
      const url = cursor
        ? `/api/projects/my?cursor=${encodeURIComponent(cursor)}`
        : '/api/projects/my'
      const res = await fetch(url, { headers: { 'X-Sync-Op': 'pull' } })
      if (!res.ok) break
      const data = (await res.json()) as {
        results: ServerProjectListItem[]
        nextCursor: string | null
      }
      serverList.push(...data.results)
      cursor = data.nextCursor
    } while (cursor !== null)
  } catch {
    // Process whatever pages were fetched before the error
  }

  const localProjects = await store.getAllProjects()
  const localById = new Map(localProjects.map((p) => [p.id, p]))
  const localPatterns = await store.getAllPatterns()

  for (const { id: serverId, updatedAt: serverUpdatedAt } of serverList) {
    const local = localById.get(serverId) ?? null

    try {
      if (local?.dirty) continue // pushDirtyProjects handles conflict resolution

      if (
        !local ||
        !local.serverUpdatedAt ||
        (serverUpdatedAt !== null && serverUpdatedAt > local.serverUpdatedAt)
      ) {
        const res = await fetch(`/api/projects/${serverId}`, { headers: { 'X-Sync-Op': 'pull' } })
        if (!res.ok) continue
        const serverProject = (await res.json()) as ServerProject
        await store.applyServerProject(buildLocalProject(serverProject, local, localPatterns))
      }
    } catch {
      // Non-fatal: skip this project, try the rest
    }
  }
}

/**
 * Run a full sync cycle. Patterns sync before projects: project push needs its
 * pattern's serverId, and project pull needs the local pattern present for id
 * mapping. No-ops if already syncing or offline.
 */
async function sync(): Promise<void> {
  if (isSyncing.value || !isOnline.value) return

  isSyncing.value = true
  syncErrors.value = []

  try {
    await pushDirty()
    await pullFromServer()
    await pushDirtyProjects()
    await pullProjects()
  } finally {
    isSyncing.value = false
    await refreshPendingChanges()
  }
}

export function useSync() {
  return {
    isSyncing: readonly(isSyncing),
    isOnline: readonly(isOnline),
    syncErrors: readonly(syncErrors),
    hasPendingChanges: readonly(hasPendingChanges),
    sync,
  }
}
